import { useState, useEffect, useCallback, useMemo } from 'react';
import RoleManagementContext from './RoleManagementContext';
import { roleManagementService } from '../../services/roleManagement/roleManagementService';
import { toast } from 'react-hot-toast';

const RoleManagementState = (props) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUserRole, setCurrentUserRole] = useState('admin');

  // Load user role from localStorage
  useEffect(() => {
    const storedRole = localStorage.getItem('sub_user_role') || 'admin';
    setCurrentUserRole(storedRole);
  }, []);

  const isRestricted = currentUserRole === 'bookings' || currentUserRole === 'operations';

  const fetchMembers = useCallback(async (isRolesLocked = false) => {
    if (isRolesLocked) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await roleManagementService.getTeamMembers();
      if (res?.success) {
        setMembers(res.data || []);
      }
    } catch (err) {
      const errMsg = err?.message || err?.error || '';
      if (
        !errMsg.toLowerCase().includes('enterprise') &&
        !errMsg.toLowerCase().includes('access locked')
      ) {
        toast.error(errMsg || 'Failed to load team members');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const addMember = useCallback(async (userData) => {
    try {
      const res = await roleManagementService.addTeamMember(userData);
      return res;
    } catch (err) {
      throw err;
    }
  }, []);

  const deleteMember = useCallback(async (id, targetRole, email) => {
    if (currentUserRole === 'subadmin' && (targetRole === 'admin' || targetRole === 'subadmin')) {
      toast.error('Subadmins are not authorized to delete Admin or Subadmin users');
      return false;
    }

    if (!window.confirm(`Are you sure you want to remove ${email}?`)) {
      return false;
    }

    try {
      await roleManagementService.deleteTeamMember(id);
      toast.success('Team member removed successfully');
      await fetchMembers();
      return true;
    } catch (err) {
      toast.error(err.message || 'Failed to remove team member');
      return false;
    }
  }, [currentUserRole, fetchMembers]);

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      if (currentUserRole === 'subadmin' && (m.role === 'admin' || m.role === 'subadmin')) {
        return false;
      }
      const query = searchQuery.toLowerCase();
      return (
        m.email.toLowerCase().includes(query) ||
        (m.phone_number && m.phone_number.includes(query)) ||
        m.role.toLowerCase().includes(query)
      );
    });
  }, [members, searchQuery, currentUserRole]);

  return (
    <RoleManagementContext.Provider
      value={{
        members,
        setMembers,
        loading,
        setLoading,
        searchQuery,
        setSearchQuery,
        currentUserRole,
        setCurrentUserRole,
        isRestricted,
        filteredMembers,
        fetchMembers,
        addMember,
        deleteMember
      }}
    >
      {props.children}
    </RoleManagementContext.Provider>
  );
};

export default RoleManagementState;
